import { Pic } from "@/components/site/Pic";

import { ArrowUpRight, Factory, Flame, Leaf, Recycle, Truck } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { Contact } from "@/components/site/Contact";
import { Reveal } from "@/components/site/Motion";
import { scrollToId } from "@/lib/scroll";
import { usePageMeta, useJsonLd } from "@/lib/pageMeta";
import { useT } from "@/i18n";

const PRODUCTS = [
  ["pellet", Flame, "Pellet di legno ad alta densità", "Ideale per stufe e caldaie residenziali: bassissimo residuo di ceneri e ottima resa termica.", "Stufe e caldaie domestiche"],
  ["bricks", Factory, "Bricchetti di abete pressato", "Un'alternativa pratica alla legna tradizionale: 100% legno vergine, senza collanti né additivi chimici. Perfetti per camini, termostufe e forni.", "Camini, termostufe e forni"],
  ["chips", Truck, "Cippato di legno", "Fornito in varie granulometrie, è la risorsa ottimale per impianti di cogenerazione, caldaie a biomassa aziendali e reti di teleriscaldamento.", "Cogenerazione e teleriscaldamento"],
];
const WHY = [["short", Recycle, "Filiera corta"], ["price", Factory, "Competitiva"], ["green", Leaf, "Sostenibile"]];

const askQuote = (t) => {
  window.dispatchEvent(new CustomEvent("prefill-quote", { detail: { project_type: "Biomassa / Bricchetti", message: t("bio2.msg", "Vorrei un preventivo per biomasse (pellet, bricchetti di abete o cippato): prodotto, quantità, luogo di consegna e frequenza di fornitura.") } }));
  scrollToId("#contatti");
};

const Hero = ({ t }) => (
  <header className="relative overflow-hidden bg-[#121110] px-5 pb-20 pt-40 text-[#F7F5F0] sm:px-8 lg:pb-28" data-testid="bio-hero">
    <Pic src="/img/emma/p-bricchetti-studio.jpg" alt={t("bio2.heroAlt", "Bricchetti di abete pressato EMMA INDUSTRIE")} className="absolute inset-0 h-full w-full object-cover opacity-40" />
    <div className="photo-fade absolute inset-0 bg-gradient-to-r from-[#121110] via-[#121110]/80 to-transparent" />
    <div className="relative mx-auto max-w-[1500px]">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#D98A52]">{t("bio2.label", "Biomasse legnose · Filiera controllata")}</p>
      <h1 className="mt-6 max-w-4xl font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">{t("bio2.h1", "Produzione biomasse: pellet, bricchetti di abete e cippato di alta qualità")}</h1>
      <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#E4DED3] sm:text-lg">{t("bio2.p", "Siamo specializzati nella produzione di biomasse legnose da filiera controllata: soluzioni ecologiche e ad alto rendimento per il riscaldamento domestico, commerciale e industriale.")}</p>
      <button onClick={() => askQuote(t)} className="btn-amber mt-10" data-testid="bio-hero-quote">{t("lett.cta", "Richiedi un preventivo")} <ArrowUpRight className="h-4 w-4" /></button>
    </div>
  </header>
);

const Intro = ({ t }) => (
  <section className="mx-auto grid max-w-[1500px] gap-10 lg:grid-cols-12" data-testid="bio-intro">
    <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl lg:col-span-5">{t("bio2.introH", "Biocombustibili efficienti, sicuri e conformi")}</h2>
    <p className="text-base leading-relaxed text-[#3D3833] sm:text-lg lg:col-span-7">{t("bio2.introP", "Gestiamo l'intero processo produttivo per garantire biocombustibili efficienti, sicuri e conformi alle più severe normative ambientali. Dai nostri impianti escono tre prodotti pensati per esigenze diverse, dalla stufa di casa alla rete di teleriscaldamento.")}</p>
  </section>
);

const Products = ({ t }) => (
  <section className="mx-auto mt-20 max-w-[1500px]" data-testid="bio-products">
    <div className="grid gap-6 md:grid-cols-3">
      {PRODUCTS.map(([k, Icon, h, p, use]) => (
        <Reveal key={k} className="flex flex-col bg-[#121110] p-6 text-[#F7F5F0] sm:p-8" data-testid={`bio-product-${k}`}>
          <Icon className="h-7 w-7 text-[#D98A52]" />
          <h3 className="mt-5 font-display text-2xl font-bold leading-tight">{t(`bio2.${k}H`, h)}</h3>
          <p className="mt-3 flex-1 text-[15px] leading-relaxed text-[#CFC8BC]">{t(`bio2.${k}`, p)}</p>
          <p className="mt-6 border-t border-white/10 pt-4 font-mono text-xs uppercase tracking-[0.18em] text-[#D98A52]">{t(`bio2.${k}U`, use)}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const Why = ({ t }) => (
  <section className="mx-auto mt-24 max-w-[1500px]" data-testid="bio-why">
    <h2 className="max-w-3xl font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("bio2.whyH", "Perché scegliere la nostra produzione")}</h2>
    <p className="mt-4 max-w-3xl text-base leading-relaxed text-[#3D3833] sm:text-lg">{t("bio2.whyP", "Scegliere le nostre biomasse significa affidarsi a una filiera corta, competitiva e sostenibile.")}</p>
    <ul className="mt-10 grid gap-px bg-[#121110]/10 sm:grid-cols-3">
      {WHY.map(([k, Icon, l]) => (
        <li key={k} className="flex items-center gap-4 bg-[#F7F5F0] p-6 font-display text-xl font-bold" data-testid={`bio-why-${k}`}><Icon className="h-6 w-6 text-[#C26D38]" />{t(`bio2.w.${k}`, l)}</li>
      ))}
    </ul>
    <button onClick={() => askQuote(t)} className="btn-amber mt-10" data-testid="bio-quote">{t("lett.cta", "Richiedi un preventivo")} <ArrowUpRight className="h-4 w-4" /></button>
  </section>
);

export default function Biomassa() {
  const t = useT();
  usePageMeta({
    title: t("bio2.metaTitle", "Produzione biomasse: pellet, bricchetti di abete e cippato"),
    desc: t("bio2.metaDesc", "Pellet ad alta densità, bricchetti di abete 100% legno vergine e cippato in varie granulometrie da filiera controllata, per riscaldamento domestico, commerciale e industriale. Richiedi un preventivo."),
    path: "/biomassa",
    image: "/img/emma/p-bricchetti-studio.jpg",
  });
  useJsonLd("biomassa", {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: PRODUCTS.map(([k, , h, p], i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: { "@type": "Product", name: t(`bio2.${k}H`, h), description: t(`bio2.${k}`, p), ...(k === "bricks" ? { image: `${window.location.origin}/img/emma/p-bricchetti-studio.jpg` } : {}), category: "Biomassa legnosa", brand: { "@type": "Brand", name: "EMMA INDUSTRIE" } },
    })),
  });
  return (
    <div className="bg-[#F7F5F0] text-[#121110]" data-testid="bio-page">
      <Nav />
      <Hero t={t} />
      <main className="px-5 py-24 sm:px-8 lg:py-32">
        <Intro t={t} />
        <Products t={t} />
        <Why t={t} />
      </main>
      <Contact />
      <Footer />
    </div>
  );
}
